import React, { useState, useMemo, useEffect } from 'react';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Modal, Form, FullNameItem, Input, Radio, CountrySelect } from 'components/elements';
import { Space } from 'antd';
import { useListPublicCountriesQuery } from 'services/apiSlices/countriesApi';
import { P } from 'components/fontSystem';
import bfbTheme from 'styles/b4b-theme';
import api from 'services/api';
import HideHubspotWidget from 'components/utils/HideHubspotWidget';

const INPUT_EMAIL = 'email';
const INPUT_ROLE = 'role';
const INPUT_CURRENT_COUNTRY = 'country';
const ROLE_MEMBER = 'Entrepreneur';
const ROLE_MENTOR = 'Mentor';

const EmailModal = props => {
  const { open, onClose, t, userData } = props;
  const [isSumtButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [isMobile, setIsMobile] = useState(null);

  const countriesData = useListPublicCountriesQuery().data;
  useEffect(() => {
    setIsMobile(document.body.clientWidth < 768);
  }, []);

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue(userData);
  }, [userData]);

  const onFormChange = () => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmitButtonDisabled(false))
      .catch(() => setIsSubmitButtonDisabled(true));
  };

  const handleOnFinish = async () => {
    const values = await form.validateFields();
    api.post('/api/maillist-contacts/', {
      email: values.email,
      first_name: values.first_name,
      last_name: values.last_name,
      country: values.country,
      user_type: values.role,
    });
    onClose();
  };

  const handleSearchSelectClick = inputName => {
    if (isMobile) {
      const label = document.querySelector(`[for="${inputName}"]`);
      label.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  const debouncedOnFormChange = useMemo(() => debounce(onFormChange, 100), []);

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      title={t('Not ready yet?')}
      okText={t('Yes, keep me posted!')}
      okButtonProps={{ disabled: isSumtButtonDisabled }}
      cancelText={t('No, thanks')}
      onOk={handleOnFinish}
      destroyOnClose
      forceRender
    >
      <StyledP>
        {t(
          "Don't worry if you're still not ready to start applying. You can share your contact here and we will keep you posted about all upcoming opportunities that may be relevant for you!"
        )}
      </StyledP>
      <Form
        form={form}
        onValuesChange={debouncedOnFormChange}
        className="white-bg"
        layout="vertical"
      >
        <FullNameItem form={form} />
        <Form.Item
          label={t('Email')}
          size="large"
          rules={[
            { required: true, message: t('Enter your email here') },
            { type: 'email', message: t('The input is not valid email') },
          ]}
          name={INPUT_EMAIL}
        >
          <Input placeholder={t('Your email')} />
        </Form.Item>
        <Form.Item
          label={t('What is your Country of Residence?')}
          className="item-margin"
          rules={[
            {
              required: true,
              message: t('Please select your current country'),
            },
          ]}
          name={INPUT_CURRENT_COUNTRY}
        >
          <CountrySelect
            countriesData={countriesData}
            t={t}
            onFocus={() => handleSearchSelectClick(INPUT_CURRENT_COUNTRY)}
          />
        </Form.Item>
        <Form.Item
          align={{
            offset: [-5, 10],
            targetOffset: ['50%', '40%'],
          }}
          label={t('Do you want to participate as Entrepreneur or Mentor?')}
          name={INPUT_ROLE}
          placement="bottomLeft"
          size="large"
        >
          <Radio.Group size="large">
            <Space direction="vertical">
              <Radio value={ROLE_MEMBER} style={{ marginBottom: '15px' }}>
                {t('Entrepreneur.')}
                <br />
                <StyledSpan>
                  {t('If you have a business idea and you are looking to develop it.')}
                </StyledSpan>
              </Radio>
              <Radio value={ROLE_MENTOR} style={{ marginBottom: '15px' }}>
                {t('Mentor')}
                <br />
                <StyledSpan>
                  {t(
                    "If you're looking to share knowledge with early stage entrepreneurs and support them in their incubation journey."
                  )}
                </StyledSpan>
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </Form>
      <HideHubspotWidget />
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  max-width: 600px;
  .ant-modal-header {
    padding: 0;
    margin-bottom: 10px;
  }

  .ant-modal-body {
    padding: 0;
  }

  &&& {
    .ant-form-item {
      margin-bottom: 22px;
    }

    .ant-form-item-label label .label-content {
      margin-bottom: 0;
    }

    .ant-btn-primary {
      background: ${bfbTheme.mainColor};
      color: white;
      height: 45px;
      margin-left: 30px;
    }

    .ant-btn-primary:disabled {
      opacity: 0.3;
    }

    .ant-btn-default {
      background: ${bfbTheme.grey};
      color: white;
      border: none;
      height: 45px;
    }
  }
`;

const StyledSpan = styled.span`
  font-size: 15px;
  font-style: italic;
`;

const StyledP = styled(P)`
  padding-bottom: 30px;
  font-weight: 500;
`;

EmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  userData: PropTypes.instanceOf(Object),
};

EmailModal.defaultProps = {
  userData: {
    first_name: '',
    last_name: '',
    email: '',
    country: '',
    role: '',
  },
};

export default EmailModal;
